import React, { useContext, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Container } from '@resurs/midas-react'
import { useParams } from 'react-router'
import OngoingPaymentDetailsUI from './OngoingPaymentDetailsUI'
import { getText } from '../../../internal/TextLabels'
import {
  annulTransaction,
  clearTransactionDetails,
  debitTransaction,
  getTransactionDetails,
  setCurrentTransactionId,
} from '../../../store/actions/transactions-actions'
import ArrowBackButton from '../../../components/common/ArrowBackButton'
import { Authorities } from '../../../models/authorities'
import { AuthContext } from '../../../context/AuthContext'
import {
  cancelPayment,
  updateAutomaticDebitDate,
} from '../../../store/actions/ongoing-payment-actions'
import { notify } from '../../../store/actions/notification-actions'
import { NOTIFY_SUCCESS } from '../../../store/actions/action-types'

const OngoingPaymentDetails = props => {
  const { hasAuthority } = useContext(AuthContext)
  const { id } = useParams()

  useEffect(() => {
    if (id) {
      props
        .getTransactionDetails(id)
        .catch(() => props.newRoute('payments/ongoing'))
    }
    return () => props.clearTransactionDetails()
  }, [])

  const [isProcessingDebit, setProcessingDebit] = useState(false)

  const isDebitable = useMemo(() => {
    if (isProcessingDebit) {
      return false
    }
    if (!props.transactionDetails?.payment?.debitable) {
      return false
    }
    if (props.transactionDetails?.signing?.signingStatus !== 'SIGNED') {
      return false
    }
    return hasAuthority(Authorities.TRANSACTIONS_DEBIT)
  }, [props.transactionDetails])

  const handleManualDebit = () => {
    if (isDebitable) {
      setProcessingDebit(true)
      props
        .debitTransaction(id)
        .then(() => {
          notify(
            NOTIFY_SUCCESS,
            getText('ongoingPayments.details.notificationPaymentDebited'),
          )
        })
        .finally(() => setProcessingDebit(false))
    }
  }

  const [isProcessingAnnul, setProcessingAnnul] = useState(false)

  const isAnnulable = useMemo(() => {
    if (isProcessingAnnul) {
      return false
    }
    if (!props.transactionDetails?.payment?.debitable) {
      return false
    }
    return hasAuthority(Authorities.TRANSACTIONS_ANNUL)
  }, [props.transactionDetails])

  const handleManualAnnul = () => {
    if (isAnnulable) {
      setProcessingAnnul(true)
      props
        .annulTransaction(id, props.transactionDetails.status)
        .then(() => {
          notify(
            NOTIFY_SUCCESS,
            getText('ongoingPayments.details.notificationPaymentAnnulled'),
          )
        })
        .finally(() => setProcessingAnnul(false))
    }
  }

  return (
    props.transactionDetails && (
      <Container>
        <ArrowBackButton buttonText={getText('ongoingPayments.title')} />
        <OngoingPaymentDetailsUI
          authorizationTimeInDays={props.authorizationTimeInDays}
          ongoingPayment={props.transactionDetails}
          isDebitable={isDebitable}
          isProcessingDebit={isProcessingDebit}
          isAnnulable={isAnnulable}
          isProcessingAnnul={isProcessingAnnul}
          onDebit={handleManualDebit}
          onAnnul={handleManualAnnul}
          updateAutomaticDebitDate={props.updateAutomaticDebitDate}
        />
      </Container>
    )
  )
}

OngoingPaymentDetails.propTypes = {
  transactionDetails: PropTypes.object,
  getTransactionDetails: PropTypes.func.isRequired,
  clearTransactionDetails: PropTypes.func.isRequired,
  debitTransaction: PropTypes.func.isRequired,
  annulTransaction: PropTypes.func.isRequired,
  newRoute: PropTypes.func.isRequired,
  updateAutomaticDebitDate: PropTypes.func.isRequired,
}

OngoingPaymentDetails.defaultProps = {
  transactionDetails: null,
}

const mapStateToProps = state => ({
  transactionDetails: state.transactions.currentTransactionDetails,
  authorizationTimeInDays:
    state.storeInfo.currentRepresentative.authorizationTimeInDays,
  loading: state.transactions.action.loading,
})

const mapDispatchToProps = dispatch => ({
  getTransactionDetails: transactionId =>
    dispatch(getTransactionDetails(transactionId)).then(() =>
      dispatch(setCurrentTransactionId(transactionId)),
    ),
  clearTransactionDetails: () => dispatch(clearTransactionDetails()),
  debitTransaction: transactionId =>
    dispatch(debitTransaction([], transactionId, null)),
  annulTransaction: (paymentId, status) =>
    dispatch(
      /**
       * Check whether the payment has terminal status.
       * If true, do a normal annul request otherwise cancel the payment in order to reject it.
       */
      ['APPROVED', 'FROZEN'].includes(status)
        ? annulTransaction([], paymentId)
        : cancelPayment(paymentId),
    ),
  updateAutomaticDebitDate: (id, debitDate) =>
    dispatch(updateAutomaticDebitDate(id, { automaticDebitDate: debitDate })),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(OngoingPaymentDetails)
